import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Select, App } from 'antd';
import { api } from '../../services/api';
import { useVendor } from '../../context/VendorContext';
import AvatarImage from '../AvatarImage';
import { getAvatarInitials, getAvatarInitialsStyle, getAvatarUrl, getMaleAvatarSeeds, getFemaleAvatarSeeds, seedToS3Key, s3KeyToSeed } from '../../utils/avatars';

const { TextArea } = Input;
const { Option } = Select;

const PERSONALITY_PRESETS = [
  {
    label: 'Skeptical & Busy',
    value: 'SKEPTICAL_AND_BUSY',
    defaultPrompt: 'Direct, concise, and protective of time. Questions vendor claims immediately and demands concrete proof and clear business outcomes.',
  },
  {
    label: 'Analytical & Detail-Oriented',
    value: 'ANALYTICAL_AND_DETAIL_ORIENTED',
    defaultPrompt: 'Methodical and numbers-driven. Digs deep into technical specs, data security, measurable ROI metrics, and implementation specifics.',
  },
  {
    label: 'Friendly & Open',
    value: 'FRIENDLY_AND_OPEN',
    defaultPrompt: 'Collaborative, warm, and conversational. Open to new ideas and partnerships, but looks for strong alignment with team culture and long-term value.',
  },
  {
    label: 'Aggressive & Cost-Focused',
    value: 'AGGRESSIVE_AND_COST_FOCUSED',
    defaultPrompt: 'Tough negotiator laser-focused on bottom-line cost, discounts, and total cost of ownership. Challenges pricing models and pushes back on premium features.',
  },
  {
    label: 'Indecisive & Risk-Averse',
    value: 'INDECISIVE_AND_RISK_AVERSE',
    defaultPrompt: 'Cautious and worried about making a mistake or disrupting workflows. Requests case studies, references, compliance assurances, and low-risk rollout plans.',
  },
];

const INDUSTRIES = [
  { label: 'SaaS', value: 'SAAS' },
  { label: 'E-Commerce', value: 'ECOMMERCE' },
  { label: 'Financial Services', value: 'FINANCIAL_SERVICES' },
  { label: 'Hospital', value: 'HOSPITAL' },
  { label: 'Manufacturing', value: 'MANUFACTURING' },
  { label: 'Retail', value: 'RETAIL' },
  { label: 'Logistics', value: 'LOGISTICS' },
  { label: 'Medical Devices', value: 'MEDICAL_DEVICES' },
  { label: 'Insurance', value: 'INSURANCE' },
  { label: 'Real Estate', value: 'REAL_ESTATE' },
  { label: 'Hospitality', value: 'HOSPITALITY' },
  { label: 'Automotive', value: 'AUTOMOTIVE' },
  { label: 'Universal', value: 'UNIVERSAL' },
];

const COMPANY_SIZES = [
  { label: '1-50', value: 'RANGE_1_50' },
  { label: '51-200', value: 'RANGE_51_200' },
  { label: '200-500', value: 'RANGE_200_500' },
  { label: '500-2000', value: 'RANGE_500_2000' },
  { label: '2000+', value: 'RANGE_2000_PLUS' },
];

const REGIONS = [
  { label: 'North America', value: 'NORTH_AMERICA' },
  { label: 'EMEA', value: 'EMEA' },
  { label: 'APAC', value: 'APAC' },
  { label: 'LATAM', value: 'LATAM' },
];

const CUSTOMER_TYPES = [
  { label: 'New Prospect', value: 'NEW_PROSPECT' },
  { label: 'Existing Customer', value: 'EXISTING_CUSTOMER' },
  { label: 'Churned Customer', value: 'CHURNED_CUSTOMER' },
];

const LANGUAGES = [
  { label: 'English', value: 'ENGLISH' },
  { label: 'German', value: 'GERMAN' },
  { label: 'Dutch', value: 'DUTCH' },
  { label: 'Italian', value: 'ITALIAN' },
  { label: 'Japanese', value: 'JAPANESE' },
  { label: 'Spanish', value: 'SPANISH' },
  { label: 'French', value: 'FRENCH' },
  { label: 'Tamil', value: 'TAMIL' },
  { label: 'Kannada', value: 'KANNADA' },
  { label: 'Hindi', value: 'HINDI' },
  { label: 'Telugu', value: 'TELUGU' },
  { label: 'Malayalam', value: 'MALAYALAM' },
];

const DEEPGRAM_VOICES = {
  FEMALE: {
    v2: [
      { label: 'Amalthea', value: 'aura-2-amalthea-en' },
      { label: 'Andromeda', value: 'aura-2-andromeda-en' },
      { label: 'Asteria', value: 'aura-2-asteria-en' },
      { label: 'Athena', value: 'aura-2-athena-en' },
      { label: 'Aurora', value: 'aura-2-aurora-en' },
      { label: 'Callista', value: 'aura-2-callista-en' },
      { label: 'Cora', value: 'aura-2-cora-en' },
      { label: 'Cordelia', value: 'aura-2-cordelia-en' },
      { label: 'Delia', value: 'aura-2-delia-en' },
      { label: 'Electra', value: 'aura-2-electra-en' },
      { label: 'Harmonia', value: 'aura-2-harmonia-en' },
      { label: 'Helena', value: 'aura-2-helena-en' },
      { label: 'Hera', value: 'aura-2-hera-en' },
      { label: 'Iris', value: 'aura-2-iris-en' },
      { label: 'Janus', value: 'aura-2-janus-en' },
      { label: 'Juno', value: 'aura-2-juno-en' },
      { label: 'Luna', value: 'aura-2-luna-en' },
      { label: 'Minerva', value: 'aura-2-minerva-en' },
      { label: 'Ophelia', value: 'aura-2-ophelia-en' },
      { label: 'Pandora', value: 'aura-2-pandora-en' },
      { label: 'Phoebe', value: 'aura-2-phoebe-en' },
      { label: 'Selene', value: 'aura-2-selene-en' },
      { label: 'Thalia', value: 'aura-2-thalia-en' },
      { label: 'Theia', value: 'aura-2-theia-en' },
      { label: 'Vesta', value: 'aura-2-vesta-en' },
    ],
    v1: [
      { label: 'Asteria', value: 'aura-asteria-en' },
      { label: 'Athena', value: 'aura-athena-en' },
      { label: 'Hera', value: 'aura-hera-en' },
      { label: 'Luna', value: 'aura-luna-en' },
      { label: 'Stella', value: 'aura-stella-en' },
    ],
  },
  MALE: {
    v2: [
      { label: 'Apollo', value: 'aura-2-apollo-en' },
      { label: 'Arcas', value: 'aura-2-arcas-en' },
      { label: 'Aries', value: 'aura-2-aries-en' },
      { label: 'Atlas', value: 'aura-2-atlas-en' },
      { label: 'Draco', value: 'aura-2-draco-en' },
      { label: 'Hermes', value: 'aura-2-hermes-en' },
      { label: 'Hyperion', value: 'aura-2-hyperion-en' },
      { label: 'Jupiter', value: 'aura-2-jupiter-en' },
      { label: 'Mars', value: 'aura-2-mars-en' },
      { label: 'Neptune', value: 'aura-2-neptune-en' },
      { label: 'Odysseus', value: 'aura-2-odysseus-en' },
      { label: 'Orion', value: 'aura-2-orion-en' },
      { label: 'Orpheus', value: 'aura-2-orpheus-en' },
      { label: 'Pluto', value: 'aura-2-pluto-en' },
      { label: 'Saturn', value: 'aura-2-saturn-en' },
      { label: 'Zeus', value: 'aura-2-zeus-en' },
    ],
    v1: [
      { label: 'Angus', value: 'aura-angus-en' },
      { label: 'Arcas', value: 'aura-arcas-en' },
      { label: 'Helios', value: 'aura-helios-en' },
      { label: 'Orion', value: 'aura-orion-en' },
      { label: 'Orpheus', value: 'aura-orpheus-en' },
      { label: 'Perseus', value: 'aura-perseus-en' },
      { label: 'Zeus', value: 'aura-zeus-en' },
    ],
  },
};

const AWS_POLLY_VOICES = {
  FEMALE: [
    { label: 'Joanna (US English)', value: 'Joanna' },
    { label: 'Danielle (US English)', value: 'Danielle' },
    { label: 'Ruth (US English)', value: 'Ruth' },
    { label: 'Salli (US English)', value: 'Salli' },
    { label: 'Tiffany (US English)', value: 'Tiffany' },
    { label: 'Amy (British English)', value: 'Amy' },
    { label: 'Olivia (Australian English)', value: 'Olivia' },
    { label: 'Kajal (Indian English)', value: 'Kajal' },
    { label: 'Niamh (Irish English)', value: 'Niamh' },
    { label: 'Aria (New Zealand English)', value: 'Aria' },
    { label: 'Jasmine (Singaporean English)', value: 'Jasmine' },
    { label: 'Ayanda (South African English)', value: 'Ayanda' },
    { label: 'Ambre (French - France)', value: 'Ambre' },
    { label: 'Céline (French - France)', value: 'Céline' },
    { label: 'Léa (French - France)', value: 'Léa' },
    { label: 'Isabelle (French - Belgium)', value: 'Isabelle' },
    { label: 'Gabrielle (French - Canada)', value: 'Gabrielle' },
    { label: 'Vicki (German - Germany)', value: 'Vicki' },
    { label: 'Hannah (German - Austria)', value: 'Hannah' },
    { label: 'Sabrina (German - Switzerland)', value: 'Sabrina' },
    { label: 'Lupe (Spanish - US)', value: 'Lupe' },
    { label: 'Lucia (Spanish - Spain)', value: 'Lucia' },
    { label: 'Mía (Spanish - Mexico)', value: 'Mía' },
    { label: 'Beatrice (Italian - Italy)', value: 'Beatrice' },
    { label: 'Bianca (Italian - Italy)', value: 'Bianca' },
    { label: 'Laura (Dutch - Netherlands)', value: 'Laura' },
    { label: 'Lisa (Dutch - Belgium)', value: 'Lisa' },
    { label: 'Camila (Portuguese - Brazil)', value: 'Camila' },
    { label: 'Seoyeon (Korean)', value: 'Seoyeon' },
    { label: 'Ewa (Polish)', value: 'Ewa' },
    { label: 'Ola (Polish)', value: 'Ola' },
  ],
  MALE: [
    { label: 'Matthew (US English)', value: 'Matthew' },
    { label: 'Stephen (US English)', value: 'Stephen' },
    { label: 'Brian (British English)', value: 'Brian' },
    { label: 'Florian (French - France)', value: 'Florian' },
    { label: 'Rémi (French - France)', value: 'Rémi' },
    { label: 'Liam (French - Canada)', value: 'Liam' },
    { label: 'Daniel (German - Germany)', value: 'Daniel' },
    { label: 'Lennart (German - Germany)', value: 'Lennart' },
    { label: 'Pedro (Spanish - US)', value: 'Pedro' },
    { label: 'Sergio (Spanish - Spain)', value: 'Sergio' },
    { label: 'Andrés (Spanish - Mexico)', value: 'Andrés' },
    { label: 'Lorenzo (Italian - Italy)', value: 'Lorenzo' },
  ],
};

const ELEVEN_LABS_MODELS = [
  { label: 'Multilingual v2', value: 'eleven_multilingual_v2', note: 'Best quality, higher latency' },
];

const VOICE_PREVIEW_LINKS = {
  eleven_labs: { label: 'Preview voices on ElevenLabs (requires login)', url: 'https://elevenlabs.io/app/voice-library?use_cases=conversational&required_languages=en' },
  deepgram: { label: 'Preview voices on Deepgram', url: 'https://playground.deepgram.com/?endpoint=speak&architecture=aura-2&language=en' },
  cartesia: { label: 'Preview voices on Cartesia (requires login)', url: 'https://play.cartesia.ai/voices?language=en' },
  aws_polly: { label: 'View AWS Polly Generative Voices Documentation', url: 'https://docs.aws.amazon.com/polly/latest/dg/generative-voices.html' },
};

const BuyerProfileModal = ({ visible, agent, onCancel, onSuccess }) => {
  const [form] = Form.useForm();
  const { selectedVendorId } = useVendor();
  const [loading, setLoading] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [selectedAvatarS3KeyLocal, setSelectedAvatarS3KeyLocal] = useState(null);
  const { message } = App.useApp() || {};

  const selectedGender = Form.useWatch('gender', form);

  useEffect(() => {
    if (visible) {
      if (agent) {
        const fieldsToSet = {
          name: agent.name || '',
          jobTitle: agent.jobTitle || '',
          companyName: agent.companyName ?? '',
          gender: agent.gender || undefined,
          personalityPreset: agent.personalityPreset || undefined,
          personalityPresetPrompt: agent.personalityPresetPrompt || '',
          industry: agent.industry || undefined,
          companySize: agent.companySize ?? '',
          region: agent.region ?? '',
          customerType: agent.customerType ?? '',
          language: agent.language ?? '',
          companyContextPrompt: agent.companyContextPrompt ?? '',
          voiceProvider: agent.voiceProvider || undefined,
          voiceModelId: agent.voiceModelId || undefined,
          voiceId: agent.voiceId || '',
          avatarS3Key: agent.avatarS3Key || undefined,
        };
        
        form.resetFields();
        form.setFieldsValue(fieldsToSet);

        let seed = s3KeyToSeed(agent.avatarS3Key);
        if (!seed) {
          seed = agent.gender === 'FEMALE' ? 'Sarah' : 'James';
        }
        setSelectedAvatarS3KeyLocal(seed);
        setSelectedProvider(agent.voiceProvider ?? null);
      } else {
        form.resetFields();
        setSelectedAvatarS3KeyLocal(null);
        setSelectedProvider(null);
      }
    }
  }, [visible, agent, form]);

  const handleProviderChange = (val) => {
    setSelectedProvider(val);
    form.setFieldValue('voiceId', undefined);
    if (val === 'deepgram' || val === 'cartesia' || val === 'aws_polly') {
      form.setFieldValue('voiceModelId', undefined);
    } else if (val === 'eleven_labs') {
      form.setFieldValue('voiceModelId', 'eleven_multilingual_v2');
    }
  };

  const handleGenderChange = (genderVal) => {
    if (selectedProvider === 'deepgram' || selectedProvider === 'aws_polly') {
      form.setFieldValue('voiceId', undefined);
    }
    if (genderVal === 'FEMALE') {
      const femaleSeeds = getFemaleAvatarSeeds();
      if (!femaleSeeds.includes(selectedAvatarS3KeyLocal)) {
        setSelectedAvatarS3KeyLocal(femaleSeeds[0] || 'Sarah');
      }
    } else if (genderVal === 'MALE') {
      const maleSeeds = getMaleAvatarSeeds();
      if (!maleSeeds.includes(selectedAvatarS3KeyLocal)) {
        setSelectedAvatarS3KeyLocal(maleSeeds[0] || 'James');
      }
    } else {
      setSelectedAvatarS3KeyLocal(null);
    }
  };

  const handlePersonalityPresetChange = (presetVal) => {
    const preset = PERSONALITY_PRESETS.find((p) => p.value === presetVal);
    if (preset) {
      const currentPrompt = form.getFieldValue('personalityPresetPrompt');
      const isDefault = !currentPrompt || PERSONALITY_PRESETS.some((p) => p.defaultPrompt === currentPrompt);
      if (isDefault) {
        form.setFieldValue('personalityPresetPrompt', preset.defaultPrompt);
      }
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);
      const payload = {
        ...values,
        avatarS3Key: seedToS3Key(selectedAvatarS3KeyLocal) || null,
        vendorId: selectedVendorId ?? null,
      };

      // Strip empty optional fields on update to prevent overwriting saved values
      if (agent?.id) {
        const optionalFields = [
          'companyName',
          'companySize',
          'region',
          'customerType',
          'language',
          'companyContextPrompt',
          'personalityPreset',
          'personalityPresetPrompt',
          'voiceModelId'
        ];
        for (const key of optionalFields) {
          if (payload[key] === '' || payload[key] === undefined || payload[key] === null) {
            delete payload[key];
          }
        }
      }

      let savedAgent;
      if (agent?.id) {
        const res = await api.updateRoleplayAgent(agent.id, payload);
        savedAgent = res.data?.data;
        message?.success?.('Buyer profile updated');
      } else {
        const res = await api.createRoleplayAgent(payload);
        savedAgent = res.data?.data;
        message?.success?.('Buyer profile created');
      }
      onSuccess(savedAgent);
      onCancel();
    } catch (err) {
      if (err?.errorFields) return;
      message?.error?.(err?.response?.data?.message || 'Failed to save buyer profile');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setSelectedProvider(null);
    onCancel();
  };

  const deepgramVoices = selectedProvider === 'deepgram' && selectedGender
    ? DEEPGRAM_VOICES[selectedGender]
    : null;

  const awsPollyVoices = selectedProvider === 'aws_polly' && selectedGender
    ? AWS_POLLY_VOICES[selectedGender]
    : null;

  const voiceDisabled = (selectedProvider === 'deepgram' || selectedProvider === 'aws_polly') && !selectedGender;

  const voicePlaceholder = !selectedGender && (selectedProvider === 'deepgram' || selectedProvider === 'aws_polly')
    ? 'Select a gender first'
    : 'Select a voice';

  const previewLink = selectedProvider ? VOICE_PREVIEW_LINKS[selectedProvider] : null;

  return (
    <Modal
      title={agent ? 'Edit Buyer Profile' : 'Add Buyer Profile'}
      open={visible}
      onOk={handleSubmit}
      onCancel={handleCancel}
      confirmLoading={loading}
      okText={agent ? 'Save' : 'Create'}
      cancelText="Cancel"
      width={640}
    >
      <Form form={form} layout="vertical" requiredMark={false}>
        <Form.Item
          name="name"
          label="Name"
          rules={[{ required: true, message: 'Please enter a name' }]}
        >
          <Input placeholder="e.g. Sarah Chen" />
        </Form.Item>

        <Form.Item
          name="jobTitle"
          label="Job Title"
          rules={[{ required: true, message: 'Please enter a job title' }]}
        >
          <Input placeholder="e.g. VP of Sales" />
        </Form.Item>

        <Form.Item
          name="companyName"
          label="Company Name"
        >
          <Input placeholder="e.g. Acme Corp" />
        </Form.Item>

        <Form.Item
          name="industry"
          label="Industry"
          rules={[{ required: true, message: 'Please select an industry' }]}
        >
          <Select placeholder="Select industry" getPopupContainer={(trigger) => trigger.parentNode}>
            {INDUSTRIES.map((i) => (
              <Option key={i.value} value={i.value}>{i.label}</Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="companySize"
          label="Company Size"
        >
          <Select placeholder="Select company size" allowClear getPopupContainer={(trigger) => trigger.parentNode}>
            {COMPANY_SIZES.map((s) => (
              <Option key={s.value} value={s.value}>{s.label}</Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="region"
          label="Region"
        >
          <Select placeholder="Select region" allowClear getPopupContainer={(trigger) => trigger.parentNode}>
            {REGIONS.map((r) => (
              <Option key={r.value} value={r.value}>{r.label}</Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="customerType"
          label="Customer Type"
        >
          <Select placeholder="Select customer type" allowClear getPopupContainer={(trigger) => trigger.parentNode}>
            {CUSTOMER_TYPES.map((c) => (
              <Option key={c.value} value={c.value}>{c.label}</Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="language"
          label="Language"
        >
          <Select placeholder="Select language" allowClear getPopupContainer={(trigger) => trigger.parentNode}>
            {LANGUAGES.map((l) => (
              <Option key={l.value} value={l.value}>{l.label}</Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="companyContextPrompt"
          label="Company Context Prompt"
        >
          <TextArea rows={3} placeholder="Context about the buyer's company, market position, challenges, etc." />
        </Form.Item>

        <Form.Item
          name="gender"
          label="Gender"
          rules={[{ required: true, message: 'Please select a gender' }]}
        >
          <Select placeholder="Select gender" onChange={handleGenderChange} getPopupContainer={(trigger) => trigger.parentNode}>
            <Option value="MALE">Male</Option>
            <Option value="FEMALE">Female</Option>
          </Select>
        </Form.Item>

        {selectedGender && (
          <div style={{ marginBottom: 24 }}>
            <div style={{ marginBottom: 8, fontSize: 14, fontWeight: 500, color: '#1a1a1a' }}>Avatar</div>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              {(selectedGender === 'MALE' ? getMaleAvatarSeeds() : getFemaleAvatarSeeds()).map((seed) => {
                const selected = selectedAvatarS3KeyLocal === seed;
                return (
                  <div
                    key={seed}
                    onClick={() => setSelectedAvatarS3KeyLocal(seed)}
                    style={{
                      width: 64,
                      height: 64,
                      borderRadius: '50%',
                      overflow: 'hidden',
                      cursor: 'pointer',
                      border: selected ? '3px solid #0842A0' : '3px solid transparent',
                      boxShadow: selected ? '0 0 0 2px #0842A033' : '0 1px 3px rgba(0,0,0,0.15)',
                      transition: 'all 0.15s',
                    }}
                  >
                    {getAvatarUrl(seed) ? (
                      <AvatarImage
                        src={getAvatarUrl(seed)}
                        alt={seed}
                        size={64}
                        className="rounded-full"
                        imgStyle={{ background: '#f0f2f5' }}
                      />
                    ) : (
                      <div
                        style={{
                          width: '100%',
                          height: '100%',
                          ...getAvatarInitialsStyle(seed),
                        }}
                      >
                        {getAvatarInitials(seed)}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <Form.Item
          name="personalityPreset"
          label="Personality Preset"
          rules={[{ required: true, message: 'Please select a personality preset' }]}
        >
          <Select
            placeholder="Select personality"
            onChange={handlePersonalityPresetChange}
            getPopupContainer={(trigger) => trigger.parentNode}
          >
            {PERSONALITY_PRESETS.map((p) => (
              <Option key={p.value} value={p.value}>{p.label}</Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="personalityPresetPrompt"
          label="Personality Preset Prompt"
          extra="Guidelines defining how this persona thinks, questions, and responds."
        >
          <TextArea rows={3} placeholder="Behavioral instructions for this personality" />
        </Form.Item>

        <Form.Item
          name="voiceProvider"
          label="Voice Provider"
          rules={[{ required: true, message: 'Please select a voice provider' }]}
        >
          <Select placeholder="Select provider" onChange={handleProviderChange} getPopupContainer={(trigger) => trigger.parentNode}>
            <Option value="eleven_labs">ElevenLabs</Option>
            <Option value="deepgram">Deepgram</Option>
            <Option value="cartesia">Cartesia</Option>
            <Option value="aws_polly">AWS Polly</Option>
          </Select>
        </Form.Item>

        {selectedProvider === 'eleven_labs' && (
          <Form.Item
            name="voiceModelId"
            label="Voice Model"
            rules={[{ required: true, message: 'Please select a voice model' }]}
          >
            <Select placeholder="Select a model" getPopupContainer={(trigger) => trigger.parentNode}>
              {ELEVEN_LABS_MODELS.map((m) => (
                <Option key={m.value} value={m.value} label={m.label}>
                  {m.label}
                  <span style={{ color: '#888', fontSize: 12, marginLeft: 6 }}>— {m.note}</span>
                </Option>
              ))}
            </Select>
          </Form.Item>
        )}

        {selectedProvider === 'eleven_labs' && (
          <Form.Item
            name="voiceId"
            label="Voice ID"
            rules={[{ required: true, message: 'Please enter a voice ID' }]}
          >
            <Input placeholder="Paste voice ID from ElevenLabs" />
          </Form.Item>
        )}

        {selectedProvider === 'deepgram' && (
          <Form.Item
            name="voiceId"
            label="Voice ID"
            rules={[{ required: true, message: 'Please select a voice' }]}
          >
            <Select placeholder={voicePlaceholder} disabled={voiceDisabled} getPopupContainer={(trigger) => trigger.parentNode}>
              {deepgramVoices ? (
                <>
                  <Select.OptGroup label="Aura 2">
                    {deepgramVoices.v2.map((v) => (
                      <Option key={v.value} value={v.value}>{v.label} ({v.value})</Option>
                    ))}
                  </Select.OptGroup>
                  <Select.OptGroup label="Aura 1">
                    {deepgramVoices.v1.map((v) => (
                      <Option key={v.value} value={v.value}>{v.label} ({v.value})</Option>
                    ))}
                  </Select.OptGroup>
                </>
              ) : null}
            </Select>
          </Form.Item>
        )}

        {selectedProvider === 'aws_polly' && (
          <Form.Item
            name="voiceId"
            label="Voice ID"
            rules={[{ required: true, message: 'Please select an AWS Polly voice' }]}
          >
            <Select placeholder={voicePlaceholder} disabled={voiceDisabled} getPopupContainer={(trigger) => trigger.parentNode}>
              {awsPollyVoices
                ? awsPollyVoices.map((v) => (
                    <Option key={v.value} value={v.value}>{v.label}</Option>
                  ))
                : null}
            </Select>
          </Form.Item>
        )}

        {selectedProvider === 'cartesia' && (
          <Form.Item
            name="voiceId"
            label="Voice ID"
            rules={[{ required: true, message: 'Please enter a voice ID' }]}
          >
            <Input placeholder="Paste voice ID from Cartesia" />
          </Form.Item>
        )}

        {previewLink && (
          <div style={{ marginTop: -16, marginBottom: 16, fontSize: 13 }}>
            <a href={previewLink.url} target="_blank" rel="noopener noreferrer">
              {previewLink.label} →
            </a>
          </div>
        )}
      </Form>
    </Modal>
  );
};

export default BuyerProfileModal;
